import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tabs, Empty, Spin, Tag } from "antd";
import { useTranslation } from "react-i18next";

import css from "./CycleDayMenuComponent.module.css";
import { cycleDayMenuActions } from "../../../store/slices/menu-slices";
import { getCurrentCycleDay } from "../../../helpers";
import CycleDayPacksSingleComponent from "./CycleDayPacksSingleComponent";
import CycleDayMenuModal from "./CycleDayMenuModal";
import { CloneFromDateMenuComponent } from "../CloneFromDateMenuComponent/CloneFromDateMenuComponent";

const CycleDayMenuComponent = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const currentLanguage = i18n.language;

    const { cycleDayPacksArray, selectedDay, status, trigger } = useSelector(
        (store) => store.cycleDayMenu
    );
    const { cycleConfig } = useSelector((store) => store.appSettings);
    const lengthDays = cycleConfig?.lengthDays || 7;
    const isLoading = status === "loading";

    // On entering the screen, open directly on today's cycle day (falls back to
    // the last-selected day, then Day 1, if the cycle window isn't configured).
    const currentCycleDay = getCurrentCycleDay(cycleConfig);
    const [activeDay, setActiveDay] = useState(() => currentCycleDay || selectedDay || 1);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        dispatch(cycleDayMenuActions.setSelectedDay(activeDay));
        // run once on mount to keep the slice in sync with the initial jump
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const endDate = (() => {
        if (!cycleConfig?.startDate || !cycleConfig?.durationMonths) return null;
        const d = new Date(cycleConfig.startDate);
        d.setMonth(d.getMonth() + cycleConfig.durationMonths);
        d.setDate(d.getDate() - 1);
        return d.toISOString().split('T')[0];
    })();

    useEffect(() => {
        dispatch(cycleDayMenuActions.getAllCycleDayMenu(activeDay));
    }, [dispatch, activeDay, trigger]);

    const handleTabChange = (key) => {
        const day = Number(key);
        setActiveDay(day);
        dispatch(cycleDayMenuActions.setSelectedDay(day));
    };

    const grouped = useMemo(() => {
        if (!cycleDayPacksArray?.length) return [];

        const map = new Map();

        for (const pack of cycleDayPacksArray) {
            const cat = pack.packCategory;
            const key = cat?.id ?? '__none__';

            if (!map.has(key)) {
                map.set(key, { category: cat ?? null, packs: [] });
            }
            map.get(key).packs.push(pack);
        }

        const groups = Array.from(map.values());

        groups.sort((a, b) => {
            if (!a.category && b.category) return 1;
            if (a.category && !b.category) return -1;
            return 0;
        });

        return groups;
    }, [cycleDayPacksArray]);

    return (
        <div className={css.wrap}>
            <div className={css.header}>
                <div className={css.cycleStats}>
                    <div className={css.stat}>
                        <span className={css.statLabel}>{t("Cycle length")}</span>
                        <span className={css.statValue}>{lengthDays} {t("Days")}</span>
                    </div>
                    {cycleConfig?.startDate && (
                        <div className={css.stat}>
                            <span className={css.statLabel}>{t("Starts")}</span>
                            <span className={css.statValue}>{cycleConfig.startDate}</span>
                        </div>
                    )}
                    {endDate && (
                        <div className={css.stat}>
                            <span className={css.statLabel}>{t("Ends")}</span>
                            <span className={css.statValue}>{endDate}</span>
                        </div>
                    )}
                    {currentCycleDay && (
                        <div className={css.stat}>
                            <span className={css.statLabel}>{t("Today")}</span>
                            <span className={css.statValue}>{t("Day")} {currentCycleDay}</span>
                        </div>
                    )}
                </div>

                <CloneFromDateMenuComponent activeDay={activeDay} />
            </div>

            <Tabs
                activeKey={String(activeDay)}
                onChange={handleTabChange}
                items={Array.from({ length: lengthDays }, (_, i) => {
                    const day = i + 1;
                    return {
                        key: String(day),
                        label: (
                            <span>
                                {t("Day")} {day}
                                {day === currentCycleDay && (
                                    <Tag color="green" style={{ marginInlineStart: 6 }}>
                                        {t("Today")}
                                    </Tag>
                                )}
                            </span>
                        ),
                        children: (
                            <div className={css.tabContent}>
                                <Spin spinning={isLoading}>
                                    {!isLoading && cycleDayPacksArray.length === 0 ? (
                                        <Empty description={t("No menu set for this day")} />
                                    ) : (
                                        <div className={css.packsList}>
                                            {grouped.map(({ category, packs }) => (
                                                <div key={category?.id ?? '__none__'} className={css.categoryGroup}>
                                                    <div className={css.categoryHeader}>
                                                        {category?.icon && (
                                                            <img
                                                                src={category.icon}
                                                                alt=""
                                                                className={css.categoryIcon}
                                                            />
                                                        )}
                                                        <span className={css.categoryName}>
                                                            {category
                                                                ? (category.name[currentLanguage] || category.name['en'])
                                                                : t('Uncategorized')}
                                                        </span>
                                                        <span className={css.categoryCount}>{packs.length}</span>
                                                        <div className={css.categoryDivider} />
                                                    </div>

                                                    {packs.map((pack) => (
                                                        <CycleDayPacksSingleComponent
                                                            key={pack.id}
                                                            pack={pack}
                                                            cycleDay={activeDay}
                                                            setIsModalOpen={setIsModalOpen}
                                                        />
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Spin>
                            </div>
                        ),
                    };
                })}
            />

            {isModalOpen && (
                <CycleDayMenuModal
                    setIsModalOpen={setIsModalOpen}
                    cycleDay={activeDay}
                />
            )}

        </div>
    );
};

export { CycleDayMenuComponent };
